import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

export type EPTooltipPlacement = 'top' | 'bottom' | 'left' | 'right'
export type EPTooltipTheme = 'dark' | 'light'

/**
 * A "chip" element
 */
@customElement('ep-tooltip')
export class EPTooltip extends LitElement {

  @property({ type: String, reflect: true })
  color: EPTooltipTheme = 'dark'

  // TODO: automatically pull placement from <overlay-trigger>
  // TODO: allow other placements, like "top-start" and "top-end"
  @property({ type: String, reflect: true })
  placement: EPTooltipPlacement = 'top'

  render() {
    return html`
      <slot name="icon"></slot>
      <span id="label">
        <slot></slot>
      </span>
      <span id="tip"></span>
    `
  }

  static styles = css`
    :host {
      --internal-max-width: var(--max-width, 160px);
      --arrow-size: 6px;
      --icon-size: 14px;
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      font-size: 12px;
      font-weight: 400;
      font-family: var(--font-prose);
      box-sizing: border-box;
      display: inline-flex;
      align-items: center;
      position: relative;
      min-block-size: 24px;
      max-inline-size: var(--internal-max-width);
      padding-inline: 9px;
      margin-bottom: var(--arrow-size);
      cursor: default;
      /* user-select: none; */
      word-break: break-word;
      border-radius: 4px;
      color: var(--color);
      background-color: var(--background);
    }

    :host([color=dark]) {
      --color: #ffffff;
      --background: #6D6D6D;
    }

    :host([color=light]) {
      --color: #24292F;
      --background: #ffffff;
      /* box-shadow: rgba(27, 31, 36, 0.12) 0px 1px 3px, rgba(66, 74, 83, 0.12) 0px 8px 24px; */
      filter: drop-shadow(rgba(27, 31, 36, 0.12) 0px 1px 3px) drop-shadow(rgba(66, 74, 83, 0.12) 0px 8px 24px);
    }

    #label {
      line-height: 1.3;
      margin-block: 5px;
    }

    #tip {
      background-color: var(--background);
      clip-path: polygon(0px -5%, 50% 100%, 100% -5%);
      height: var(--arrow-size);
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      width: calc(2 * var(--arrow-size));
    }

    :host([placement=top]) #tip {
      top: 99%;
    }

    :host([placement=bottom]) #tip {
      bottom: 99%;
      clip-path: polygon(50% 0px, 0px 105%, 100% 105%);
      top: auto;
    }

    :host([placement=left]) #tip {
      clip-path: polygon(-5% 0px, -5% 100%, 100% 50%);
      top: 50%;
      left: 99%;
      transform: translateY(-50%);
      width: calc(1 * var(--arrow-size));
      height: calc(2 * var(--arrow-size));
    }

    :host([placement=right]) #tip {
      clip-path: polygon(0px 50%, 105% 100%, 105% 0px);
      top: 50%;
      left: calc(-1 * var(--arrow-size));
      right: 99%;
      transform: translateY(-50%);
      width: calc(1 * var(--arrow-size));
      height: calc(2 * var(--arrow-size));
    }

    ::slotted([slot=icon]) {
      align-self: flex-start;
      flex-shrink: 0;
      height: var(--icon-size);
      width: var(--icon-size);
      margin-block-start: 4px;
      margin-inline-start: -2px;
      margin-inline-end: 7px;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-tooltip': EPTooltip
  }
}
